import { useEffect, useMemo, useState } from "react";
import { Loader2, Download, Search, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { api } from "@/lib/api";
import type { Student } from "@/types/database";

export default function AdminStudentsPage() {
  const [rows, setRows] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 25;

  async function load() {
    setLoading(true);
    const { data, error } = await supabase
      .from("students")
      .select("*")
      .order("created_at", { ascending: false })
      .range(page * pageSize, page * pageSize + pageSize - 1);
    if (!error) setRows((data ?? []) as Student[]);
    setLoading(false);
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [page]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const t = search.toLowerCase();
    return rows.filter((r) => r.full_name.toLowerCase().includes(t) || r.email.toLowerCase().includes(t));
  }, [rows, search]);

  async function exportCsv() {
    const res = await api.adminExportCsv("students");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "students.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  async function remove(s: Student) {
    if (!confirm(`Remove ${s.full_name}? This deletes their attempts too.`)) return;
    await supabase.from("students").delete().eq("id", s.id);
    void load();
  }

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl text-white">Students</h1>
          <p className="text-steel-300">Search, view, export, or delete test entries.</p>
        </div>
        <button onClick={exportCsv} className="btn-outline"><Download className="w-4 h-4" /> Export CSV</button>
      </header>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-3 text-steel-400" />
          <input className="input pl-9" placeholder="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button onClick={() => setPage(Math.max(0, page - 1))} className="btn-outline" disabled={page === 0}>Prev</button>
        <button onClick={() => setPage(page + 1)} className="btn-outline" disabled={rows.length < pageSize}>Next</button>
      </div>

      <div className="card-dark overflow-hidden">
        {loading ? (
          <div className="p-6 text-steel-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-steel-900/60 text-steel-300 uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">School</th>
                <th className="text-left p-3">Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-steel-700">
              {filtered.map((s) => (
                <tr key={s.id}>
                  <td className="p-3 font-medium">{s.full_name}</td>
                  <td className="p-3 text-steel-300">{s.email}</td>
                  <td className="p-3 text-steel-300">{s.school_id ?? "—"}</td>
                  <td className="p-3 text-steel-300">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => remove(s)} className="text-rose-400 hover:underline inline-flex items-center gap-1">
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-steel-400">No students yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
